import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Loader2, Users, Building2, CheckCircle2, Clock, TrendingUp, Timer, ChevronLeft } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import { getDashboardData } from '../api';

function Stat({ label, value, tint = '#60A5FA', icon: Icon }) {
  return (
    <div className="panel px-3 py-2.5 flex items-center gap-2">
      {Icon && (
        <div className="icon-chip shrink-0" style={{ background: `${tint}18`, color: tint, border: `1px solid ${tint}30`, width: 32, height: 32 }}>
          <Icon size={15} />
        </div>
      )}
      <div className="min-w-0">
        <div className="text-slate-400 text-[10.5px] font-semibold truncate">{label}</div>
        <div className="text-white text-[15px] font-extrabold tabular-nums leading-tight">{value}</div>
      </div>
    </div>
  );
}

export default function RegionDrawer({ open, filterKey, filterValue, onClose, allFilters }) {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !filterValue) return;
    let cancelled = false;
    setLoading(true); setData(null);
    const body = { ...(allFilters || {}), [filterKey]: filterValue };
    getDashboardData(body)
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, filterKey, filterValue, allFilters]);

  // ESC key closes the drawer
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const kpis = data?.kpis || {};
  const cats = data?.ticketsByType || [];
  const topSchools = data?.topSchools || [];
  const topContractors = data?.topContractors || [];
  const trend = data?.trend || [];
  const fmt = (n) => (n ?? 0).toLocaleString('en-US');
  const kindLabel = filterKey === 'region' ? 'منطقة' : 'محافظة';

  return (
    <div dir="rtl" className="fixed inset-0 z-[9999]" data-testid="region-drawer">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-in fade-in" onClick={onClose} data-testid="region-drawer-backdrop" />

      {/* Drawer */}
      <div className="absolute top-0 left-0 h-full w-full max-w-[620px] bg-gradient-to-br from-[#0F1729] to-[#0A101C] shadow-2xl border-l border-white/10 overflow-y-auto flex flex-col">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-[#0F1729]/95 backdrop-blur-md border-b border-white/5 px-5 py-4 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="pill pill-blue">{kindLabel}</span>
              <div className="text-slate-400 text-[11px]">لوحة تحليلية تفصيلية</div>
            </div>
            <h2 className="text-white text-[20px] font-extrabold tracking-tight mt-0.5 truncate">{filterValue}</h2>
          </div>
          <button
            onClick={onClose}
            data-testid="region-drawer-close"
            className="panel panel-hoverable px-3 h-10 flex items-center gap-1.5 text-slate-200 hover:text-white"
            title="إغلاق (Esc)"
          >
            <X size={16} />
            <span className="text-[12px] font-semibold">إغلاق</span>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 p-5 space-y-4">
          {loading && (
            <div className="panel p-10 flex items-center justify-center gap-2 text-slate-300">
              <Loader2 size={18} className="animate-spin" /> جاري تحميل البيانات...
            </div>
          )}

          {data && (
            <>
              {/* KPI grid */}
              <div>
                <div className="text-slate-300 text-[12px] font-bold mb-2">المؤشرات الرئيسية</div>
                <div className="grid grid-cols-3 gap-2">
                  <Stat label="إجمالي المدارس" value={fmt(kpis.totalSchools)} tint="#60A5FA" icon={Building2} />
                  <Stat label="إجمالي البلاغات" value={fmt(kpis.registeredTickets)} tint="#F59E0B" icon={TrendingUp} />
                  <Stat label="المكتملة" value={fmt(kpis.completedTickets)} tint="#22C55E" icon={CheckCircle2} />
                  <Stat label="المفتوحة" value={fmt(kpis.openTickets)} tint="#EF4444" icon={Clock} />
                  <Stat label="المدارس المتأخرة" value={fmt(kpis.delayedSchools)} tint="#F87171" icon={Users} />
                  <Stat label="متوسط مدة الإغلاق" value={`${kpis.avgCloseDays ?? 0} ي`} tint="#F59E0B" icon={Timer} />
                </div>
                <div className="panel px-3 py-2.5 mt-2 flex items-center justify-between">
                  <span className="text-slate-400 text-[12px] font-semibold">نسبة الإنجاز</span>
                  <div className="flex items-center gap-3 flex-1 max-w-[380px]">
                    <div className="flex-1 h-2 bg-white/[0.05] rounded-full overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${kpis.completionRate ?? 0}%`, background: 'linear-gradient(90deg,#22C55E,#4ADE80)', boxShadow: '0 0 12px rgba(34,197,94,0.3)' }} />
                    </div>
                    <span className="text-white font-extrabold tabular-nums text-[15px]">{kpis.completionRate ?? 0}%</span>
                  </div>
                </div>
              </div>

              {/* Categories */}
              {cats.length > 0 && (
                <div>
                  <div className="text-slate-300 text-[12px] font-bold mb-2">توزيع البلاغات حسب الفئة</div>
                  <div className="panel p-3 flex items-center gap-4">
                    <div style={{ width: 130, height: 130 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie data={cats} innerRadius={40} outerRadius={60} paddingAngle={2} dataKey="value">
                            {cats.map((e, i) => <Cell key={i} fill={e.color} stroke="none" />)}
                          </Pie>
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <ul className="flex-1 space-y-1.5 min-w-0">
                      {cats.slice(0, 6).map((c) => (
                        <li key={c.name} className="flex items-center gap-2 text-[12px]">
                          <span className="w-2.5 h-2.5 rounded-full" style={{ background: c.color }} />
                          <span className="text-slate-200 flex-1 truncate">{c.name}</span>
                          <span className="text-white font-bold tabular-nums">{c.value.toLocaleString('en-US')}</span>
                          <span className="text-slate-400 tabular-nums text-[11px]">{c.percent}%</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {/* Trend */}
              {trend.length > 0 && (
                <div>
                  <div className="text-slate-300 text-[12px] font-bold mb-2">اتجاه البلاغات — أخر 7 أيام</div>
                  <div className="panel p-3" style={{ height: 160 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={trend} margin={{ top: 10, right: 10, bottom: 5, left: 0 }}>
                        <CartesianGrid stroke="rgba(255,255,255,0.08)" strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="day" tick={{ fill: '#94A3B8', fontSize: 10.5 }} axisLine={false} tickLine={false} reversed />
                        <YAxis tick={{ fill: '#94A3B8', fontSize: 10.5 }} axisLine={false} tickLine={false} orientation="right" width={30} allowDecimals={false} />
                        <Tooltip contentStyle={{ background: '#141922', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, fontSize: 12 }} />
                        <Line type="monotone" dataKey="value" stroke="#60A5FA" strokeWidth={2.5} dot={{ r: 3, fill: '#3B82F6', stroke: '#141922', strokeWidth: 2 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {/* Top schools */}
              {topSchools.length > 0 && (
                <div>
                  <div className="text-slate-300 text-[12px] font-bold mb-2">أعلى 10 مدارس بلاغاً</div>
                  <div className="panel divide-y divide-white/[0.04]">
                    {topSchools.slice(0, 10).map((s, i) => (
                      <button key={i} onClick={() => nav(`/school/${encodeURIComponent(s.id || s.name || '')}`)}
                        className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-white/[0.03] transition-colors text-right">
                        <span className="text-slate-500 text-[11px] w-4">{i + 1}</span>
                        <div className="flex-1 min-w-0">
                          <div className="text-slate-100 text-[12.5px] font-semibold truncate" title={s.name}>{s.name}</div>
                          <div className="text-slate-400 text-[10.5px]">{s.province}</div>
                        </div>
                        <span className="pill pill-red">{s.tickets}</span>
                        <ChevronLeft size={14} className="text-slate-500" />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Top contractors */}
              {topContractors.length > 0 && (
                <div>
                  <div className="text-slate-300 text-[12px] font-bold mb-2">أفضل المقاولين</div>
                  <div className="panel divide-y divide-white/[0.04]">
                    {topContractors.slice(0, 10).map((c, i) => (
                      <div key={i} className="flex items-center gap-3 px-3 py-2.5">
                        <span className="text-slate-500 text-[11px] w-4">{i + 1}</span>
                        <div className="flex-1 min-w-0">
                          <div className="text-slate-100 text-[12.5px] font-semibold truncate" title={c.name}>{c.name}</div>
                          <div className="text-slate-400 text-[10.5px] tabular-nums">{c.tickets} بلاغ • تم إنجاز {c.completed}</div>
                        </div>
                        <span className={`pill ${c.completionRate >= 90 ? 'pill-green' : c.completionRate >= 60 ? 'pill-blue' : 'pill-amber'}`}>{c.completionRate}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
